import { useState, useEffect } from "react";
import { format } from "date-fns";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Calendar as CalendarComponent } from "@/components/ui/calendar";
import { Calendar, Clock, Info } from "lucide-react";

interface RouteStartTimeDialogProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (startTime: Date) => void;
}

export function RouteStartTimeDialog({
  isOpen,
  onOpenChange,
  onSubmit,
}: RouteStartTimeDialogProps) {
  const today = new Date();
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(today);
  const [timeInput, setTimeInput] = useState(
    format(new Date(), "HH:mm") // Current time in 24-hour format
  );
  const [error, setError] = useState<string | null>(null);

  const localTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  // Reset form when dialog opens
  useEffect(() => {
    if (isOpen) {
      setSelectedDate(today);
      setTimeInput(format(new Date(), "HH:mm"));
      setError(null);
    }
  }, [isOpen]);

  const handleSubmit = () => {
    if (!selectedDate) {
      setError("Please select a date");
      return;
    }

    // Parse the time input
    const [hours, minutes] = timeInput.split(":").map(Number);
    if (
      isNaN(hours) ||
      isNaN(minutes) ||
      hours < 0 ||
      hours > 23 ||
      minutes < 0 ||
      minutes > 59
    ) {
      setError("Please enter a valid time in 24-hour format (HH:MM)");
      return;
    }

    // Create the combined date and time
    const startTime = new Date(selectedDate);
    startTime.setHours(hours, minutes, 0, 0);

    // Validate that the start time is not in the past
    if (startTime < new Date()) {
      setError("Start time cannot be in the past");
      return;
    }

    onSubmit(startTime);
    onOpenChange(false);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Set Route Start Time</DialogTitle>
          <DialogDescription>
            Choose when the trip should start. This affects ETA calculations.
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4 py-4">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Label htmlFor="date" className="text-right">
                Date
              </Label>
              <div className="flex-1">
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className="w-full justify-start text-left font-normal"
                    >
                      <Calendar className="mr-2 h-4 w-4" />
                      {selectedDate ? (
                        format(selectedDate, "PPP")
                      ) : (
                        <span>Pick a date</span>
                      )}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0">
                    <CalendarComponent
                      mode="single"
                      selected={selectedDate}
                      onSelect={setSelectedDate}
                      initialFocus
                      disabled={(date) =>
                        date < new Date(today.setHours(0, 0, 0, 0))
                      }
                    />
                  </PopoverContent>
                </Popover>
              </div>
            </div>
          </div>

          <div>
            <div className="flex items-center gap-2">
              <Label htmlFor="time" className="text-right">
                Time (24h)
              </Label>
              <div className="flex-1">
                <div className="flex items-center">
                  <Clock className="mr-2 h-4 w-4 text-gray-500" />
                  <Input
                    id="time"
                    value={timeInput}
                    onChange={(e) => setTimeInput(e.target.value)}
                    placeholder="HH:MM"
                    pattern="[0-9]{2}:[0-9]{2}"
                    className="col-span-3"
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Info className="h-4 w-4" />
            <span>All times are in your local timezone ({localTimezone})</span>
          </div>

          {error && (
            <div className="text-destructive text-sm mt-2">{error}</div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button type="submit" onClick={handleSubmit}>
            Apply
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
